import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '../config/api';
import TraderBrandMark from '../components/trader/TraderBrandMark';
import TradersEmptyState from '../components/trader/TradersEmptyState';
import { traderProfileImageUrl } from '../utils/mediaUrl';

/** فهرست بازرگان‌های تأییدشده — `/traders` */
export default function TradersDirectory() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/public/traders?limit=48'));
      let body;
      try {
        body = await res.json();
      } catch {
        setItems([]);
        return;
      }
      if (!res.ok || !body.success) {
        setItems([]);
        return;
      }
      setItems(Array.isArray(body.data?.items) ? body.data.items : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Helmet>
        <title>{t('tradersDirectory.pageTitle')}</title>
        <meta name="description" content={t('tradersDirectory.subtitle')} />
      </Helmet>

      <div className="min-h-screen bg-linen-canvas py-10 md:py-14">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <div className="mb-10 max-w-2xl">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-signal">
              {t('tradersDirectory.eyebrow')}
            </p>
            <h1 className="type-display text-3xl md:text-4xl">
              {t('tradersDirectory.title')}
            </h1>
            <p className="mt-3 text-steel">{t('tradersDirectory.subtitle')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-12 w-12 animate-spin rounded-full border-2 border-hairline border-t-signal" />
            </div>
          ) : null}

          {!loading && items.length === 0 ? <TradersEmptyState /> : null}

          {!loading && items.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((trader) => {
                const initial = (trader.companyName || '?').trim().charAt(0);
                const href = `/trader/${encodeURIComponent(trader.publicSlug)}`;
                const logoUrl = traderProfileImageUrl(trader.profileImage);
                return (
                  <article
                    key={trader.publicSlug}
                    className="flex flex-col rounded-[1.25rem] border border-hairline/50 bg-card-surface p-6 shadow-saas transition hover:-translate-y-0.5 hover:border-signal/25"
                  >
                    <div className="mb-4">
                      <TraderBrandMark
                        imageUrl={logoUrl}
                        initial={initial}
                        avatar={{
                          className:
                            'flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-cta-gradient text-2xl font-bold text-white shadow-saas',
                        }}
                        size="card"
                      />
                      <div className="mt-4 min-w-0">
                        <h2 className="type-card-title">{trader.companyName}</h2>
                        <p className="mt-0.5 text-xs text-steel">{t('traderPublic.verifiedMerchant')}</p>
                      </div>
                    </div>
                    {trader.landingBlurb ? (
                      <p className="mb-4 line-clamp-3 flex-1 text-sm leading-relaxed text-steel">{trader.landingBlurb}</p>
                    ) : (
                      <div className="flex-1" />
                    )}
                    {trader.address ? (
                      <p className="mb-4 truncate text-xs text-steel">{trader.address}</p>
                    ) : null}
                    <div className="flex items-center justify-between gap-3 pt-2">
                      <span className="text-xs text-steel">
                        {t('tradersDirectory.productCount', { count: trader.productCount || 0 })}
                      </span>
                      <Link
                        to={href}
                        className="rounded-full bg-signal/10 px-4 py-2 text-sm font-semibold text-signal transition hover:bg-signal/15"
                      >
                        {t('tradersDirectory.viewStore')}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
